<template>
  <BaseSelect
    ref="selectRef"
    v-bind="$attrs"
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :invalid="invalid"
    :external-search="true"
    :editable="editable"
    :edit-title="editTitle"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @edit-option="openEditModal"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />

  <AddCounterpartyModal
    v-if="isEditModalVisible"
    :counterparty-id="editingCounterpartyId"
    @counterparty-updated="handleCounterpartyUpdated"
    @close="closeEditModal"
  />
</template>

<script>
import { mapState } from "pinia";

import { useCounterpartyStore } from "@/stores/counterpartyStore";
import AddCounterpartyModal from "@/components/Counterparty/AddCounterpartyModal.vue";

export default {
  name: "CounterpartySelect",

  inheritAttrs: false,

  components: {
    AddCounterpartyModal,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите контрагента",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить нового контрагента",
    },
    noResultsText: {
      type: String,
      default: "Контрагенты не найдены",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
    // Прокидывается в BaseSelect для кнопки редактирования выбранного контрагента
    editTitle: {
      type: String,
      default: "Редактировать контрагента",
    },
  },

  emits: [
    "update:modelValue",
    "change",
    "button-click",
    "edit-option",
    "counterparty-updated",
  ],

  data() {
    return {
      preselectedOption: null,
      isSearchActive: false,
      isEditModalVisible: false,
      editingCounterpartyId: null,
    };
  },

  computed: {
    ...mapState(useCounterpartyStore, [
      "counterparties",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.counterparties
        .filter((counterparty) => {
          if (
            this.modelValue &&
            String(counterparty.id) === String(this.modelValue)
          )
            return true;
          return !this.excludeIds.includes(counterparty.id);
        })
        .map((counterparty) => ({
          value: counterparty.id,
          label: counterparty.selector_name,
        }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useCounterpartyStore();
      await store.fetchCounterparties({ search: searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useCounterpartyStore();
      await store.fetchCounterparties();
    },

    async handleScrollEnd() {
      const store = useCounterpartyStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.dataIsLoading
      ) {
        await store.loadMoreCounterparties();
      }
    },

    openEditModal(counterpartyId) {
      if (!counterpartyId) return;

      this.editingCounterpartyId = counterpartyId;
      this.isEditModalVisible = true;
      this.$emit("edit-option", counterpartyId);
    },

    closeEditModal() {
      this.isEditModalVisible = false;
      this.editingCounterpartyId = null;
    },

    handleCounterpartyUpdated(counterparty) {
      const store = useCounterpartyStore();
      store.replaceCounterparty(counterparty);

      if (String(counterparty.id) === String(this.modelValue)) {
        this.preselectedOption = {
          value: counterparty.id,
          label: counterparty.selector_name,
        };
      }

      this.$nextTick(() => {
        this.$refs.selectRef?.reselectSelectedOption();
      });
      this.$emit("counterparty-updated", counterparty);
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      // Если уже есть в отформатированных опциях — берём оттуда
      const existsInOptions = this.formattedOptions.find(
        (o) => String(o.value) === String(this.modelValue)
      );
      if (existsInOptions) {
        this.preselectedOption = null;
        return;
      }

      const store = useCounterpartyStore();
      const exists = store.counterparties.find(
        (c) => String(c.id) === String(this.modelValue)
      );
      if (exists) {
        this.preselectedOption = {
          value: exists.id,
          label: exists.selector_name,
        };
        store.counterparties = store.counterparties.filter(
          (c) => String(c.id) !== String(this.modelValue)
        );
        return;
      }

      try {
        await store.ensureCounterparty(this.modelValue);
        const found = store.counterparties.find(
          (c) => String(c.id) === String(this.modelValue)
        );
        if (found) {
          this.preselectedOption = {
            value: found.id,
            label: found.selector_name,
          };
          store.counterparties = store.counterparties.filter(
            (c) => String(c.id) !== String(this.modelValue)
          );
        }
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного контрагента:",
          error
        );
      }
    },
  },

  async mounted() {
    const store = useCounterpartyStore();
    await store.fetchCounterparties();
    await this.fetchPreselectedOption();
  },
};
</script>
